import { useState, useEffect, useMemo } from "react";
import type { FileData } from "../../api/graphql";
import { imageCache } from "../services/imageCache";

export interface UseAuthenticatedImageResult {
  imageUrl: string | null;
  loading: boolean;
  error: Error | null;
}

export function useAuthenticatedImage(
  fileData: FileData | null | undefined,
  entityName: string,
  fieldName: string,
  entityId: string,
  cacheVersion?: number
): UseAuthenticatedImageResult {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fileName = fileData?.name;

  const cacheParams = useMemo(() => {
    return fileName
      ? { entityName, fieldName, entityId, fileName, cacheVersion }
      : null;
  }, [fileName, entityName, fieldName, entityId, cacheVersion]);

  useEffect(() => {
    if (!cacheParams) {
      setImageUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;

    const loadImage = async () => {
      setLoading(true);
      setError(null);

      try {
        const blobUrl = await imageCache.getImage(
          cacheParams.entityName,
          cacheParams.fieldName,
          cacheParams.entityId,
          cacheParams.fileName
        );

        if (isMounted) {
          setImageUrl(blobUrl);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error("Unknown error"));
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [cacheParams]);

  return { imageUrl, loading, error };
}
